/**
 * LOGOS Daily Check-in — Guided Domain-by-Domain State Update
 * 
 * Activates when the system detects the user's last check-in is >24h old.
 * Walks through 7 domains with 4 sliders each for rapid state capture.
 * 
 * @author Dr. José Manuel Cadena Ortiz de Montellano
 * @framework Cadena Strategic Systems
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DOMAINS } from '../domain/constants';

const DOMAIN_ORDER = ['physical', 'emotional', 'mental', 'spiritual', 'relational', 'purpose', 'alimento'];

const fonts = {
  mono: "'JetBrains Mono', 'Fira Code', monospace",
  sans: "'Inter', -apple-system, sans-serif",
};

const COLORS = {
  bg: '#0D0B1A',
  card: 'rgba(255,255,255,0.04)',
  border: 'rgba(255,255,255,0.08)',
  text: '#E8E4F0',
  textMuted: '#8B85A0',
  accent: '#C084FC',
};

export default function DailyCheckin({ currentState, onComplete, onSkip }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [localState, setLocalState] = useState(() => ({ ...currentState }));
  const [sameAsYesterday, setSameAsYesterday] = useState({});

  const domainKey = DOMAIN_ORDER[step];
  const domain = DOMAINS[domainKey];
  const totalSteps = DOMAIN_ORDER.length;
  const isLast = step === totalSteps - 1;

  const handleSlider = useCallback((dim, value) => {
    setLocalState(prev => ({ ...prev, [dim]: parseFloat(value) }));
  }, []);

  const handleSameAsYesterday = useCallback(() => {
    setSameAsYesterday(prev => ({ ...prev, [domainKey]: true }));
    if (isLast) {
      onComplete(localState);
    } else {
      setStep(s => s + 1);
    }
  }, [domainKey, isLast, localState, onComplete]);

  const handleNext = useCallback(() => {
    if (isLast) {
      onComplete(localState);
    } else {
      setStep(s => s + 1);
    }
  }, [isLast, localState, onComplete]);

  const handleBack = useCallback(() => {
    if (step > 0) setStep(s => s - 1);
  }, [step]);

  if (!domain) return null;

  const dims = Object.entries(domain.dims);
  const domainAvg = dims.reduce((s, [k]) => s + (localState[k] || 0.5), 0) / dims.length;

  // Time-aware greeting
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Buenos días' : hour < 18 ? 'Buenas tardes' : 'Buenas noches';
  const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  const today = dayNames[new Date().getDay()];

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }}>
      <div style={{
        background: `linear-gradient(135deg, ${COLORS.bg} 0%, #1a1033 100%)`,
        border: `1px solid ${domain.color}30`,
        borderRadius: 20, maxWidth: 440, width: '100%',
        overflow: 'hidden', position: 'relative',
      }}>
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${domain.color}15, transparent)`,
          padding: '20px 24px 16px',
          borderBottom: `1px solid ${domain.color}20`,
        }}>
          {step === 0 && (
            <div style={{ marginBottom: 12 }}>
              <h2 style={{
                fontFamily: fonts.sans, fontSize: 18, color: COLORS.text,
                margin: '0 0 4px', fontWeight: 600,
              }}>
                {greeting} ✦
              </h2>
              <p style={{
                fontFamily: fonts.sans, fontSize: 12, color: COLORS.textMuted,
                margin: 0, lineHeight: 1.5,
              }}>
                Es {today}. Tu último check-in fue hace más de 24h.
                Actualicemos tu estado de consciencia.
              </p>
            </div>
          )}

          {/* Progress bar */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
            {DOMAIN_ORDER.map((dk, i) => (
              <div key={dk} style={{
                flex: 1, height: 3, borderRadius: 2,
                background: i < step ? DOMAINS[dk].color
                  : i === step ? `${DOMAINS[dk].color}88`
                  : 'rgba(255,255,255,0.08)',
                transition: 'all 0.3s ease',
              }} />
            ))}
          </div>

          {/* Domain header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              fontSize: 28, width: 44, height: 44,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: `${domain.color}15`, borderRadius: 12,
              border: `1px solid ${domain.color}30`,
            }}>
              {domain.icon}
            </span>
            <div>
              <h3 style={{
                fontFamily: fonts.mono, fontSize: 11, color: domain.color,
                margin: 0, letterSpacing: '0.12em', textTransform: 'uppercase',
              }}>
                {domain.label}
              </h3>
              <span style={{
                fontFamily: fonts.mono, fontSize: 9, color: COLORS.textMuted,
              }}>
                Paso {step + 1} de {totalSteps} · Promedio: {(domainAvg * 100).toFixed(0)}%
              </span>
            </div>
          </div>
        </div>

        {/* Sliders */}
        <div style={{ padding: '16px 24px 8px' }}>
          {dims.map(([dimKey, dimInfo]) => {
            const val = localState[dimKey] ?? 0.5;
            return (
              <div key={dimKey} style={{ marginBottom: 16 }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', marginBottom: 4,
                }}>
                  <label style={{
                    fontFamily: fonts.sans, fontSize: 12, color: COLORS.text,
                    fontWeight: 500,
                  }}>
                    {dimInfo.label}
                  </label>
                  <span style={{
                    fontFamily: fonts.mono, fontSize: 11, color: domain.color,
                    fontWeight: 600, minWidth: 36, textAlign: 'right',
                  }}>
                    {(val * 100).toFixed(0)}%
                  </span>
                </div>
                <p style={{
                  fontFamily: fonts.sans, fontSize: 10, color: COLORS.textMuted,
                  margin: '0 0 6px', lineHeight: 1.3,
                }}>
                  {dimInfo.desc}
                </p>
                <input
                  type="range" min="0" max="1" step="0.01"
                  value={val}
                  onChange={(e) => handleSlider(dimKey, e.target.value)}
                  style={{
                    width: '100%', height: 6, borderRadius: 3,
                    appearance: 'none', outline: 'none',
                    background: `linear-gradient(to right, ${domain.color} ${val * 100}%, rgba(255,255,255,0.1) ${val * 100}%)`,
                    cursor: 'pointer',
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div style={{
          padding: '8px 24px 20px',
          display: 'flex', gap: 10, justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {step > 0 && (
              <button onClick={handleBack} style={{
                background: 'rgba(255,255,255,0.06)',
                border: `1px solid ${COLORS.border}`,
                borderRadius: 8, padding: '8px 14px',
                color: COLORS.textMuted, fontFamily: fonts.mono,
                fontSize: 10, cursor: 'pointer',
              }}>
                {t('onboarding.previous', '← Atrás')}
              </button>
            )}
            <button onClick={handleSameAsYesterday} style={{
              background: 'rgba(255,255,255,0.04)',
              border: `1px solid ${COLORS.border}`,
              borderRadius: 8, padding: '8px 14px',
              color: COLORS.textMuted, fontFamily: fonts.mono,
              fontSize: 10, cursor: 'pointer',
            }}>
              {t('misc.sameAsYesterday')}
            </button>
          </div>

          <button onClick={handleNext} style={{
            background: `linear-gradient(135deg, ${domain.color}, ${domain.color}88)`,
            border: 'none', borderRadius: 8, padding: '8px 20px',
            color: '#fff', fontFamily: fonts.mono, fontSize: 10,
            cursor: 'pointer', fontWeight: 600, letterSpacing: '0.05em',
          }}>
            {isLast ? t('misc.completeCheckin') : t('misc.nextStep')}
          </button>
        </div>

        {/* Skip link */}
        <div style={{ textAlign: 'center', paddingBottom: 16 }}>
          <button onClick={onSkip} style={{
            background: 'none', border: 'none',
            color: COLORS.textMuted, fontFamily: fonts.mono,
            fontSize: 9, cursor: 'pointer', opacity: 0.6,
            textDecoration: 'underline',
          }}>
            Omitir por ahora
          </button>
        </div>
      </div>

      {/* Slider thumb styles */}
      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 18px; height: 18px;
          border-radius: 50%;
          background: white;
          border: 2px solid ${domain.color};
          box-shadow: 0 0 8px ${domain.color}44;
          cursor: pointer;
        }
      `}</style>
    </div>
  );
}
